import { useEffect, useMemo, useRef, useState } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { Dialog, DialogContent } from '@duck/ui'
import { ChallengeService } from '@duck/api-definitions'
import { apis } from '@modules/apiClients'
import {
  workspaceSettingsOpenAtom,
  workspaceSettingsInitialTabAtom,
  workspaceChallengeAtom,
  workspaceTaskAtom,
} from '@/store/workspaceAtoms'
import { trackWorkspaceSettings } from '@/modules/workspaceSettingsAnalytics'
import { useUserVoice } from '@/hooks/useUserVoice'
import { WorkspaceSettingsNav, type SettingsNavKey } from './WorkspaceSettingsNav'
import { ChallengePanel } from './panels/ChallengePanel'
import { AppearancePanel } from './panels/AppearancePanel'
import { BehaviorPanel } from './panels/BehaviorPanel'
import { PythonPanel } from './panels/PythonPanel'
import { ShortcutsPanel } from './panels/ShortcutsPanel'

export const WorkspaceSettingsDialog = () => {
  const [open, setOpen] = useAtom(workspaceSettingsOpenAtom)
  const [initialTab, setInitialTab] = useAtom(workspaceSettingsInitialTabAtom)
  const task = useAtomValue(workspaceTaskAtom)
  const [challenge, setChallenge] = useAtom(workspaceChallengeAtom)
  const { openVoice } = useUserVoice()

  const allowedWeapons = useMemo<ChallengeService.TaskWeaponsOfChoiceEnum[]>(
    () => task?.allowedWeapons ?? [ChallengeService.TaskWeaponsOfChoiceEnum.Sql],
    [task?.allowedWeapons]
  )
  const chosenWeapon =
    challenge?.workState?.chosenWeapon ??
    allowedWeapons[0] ??
    ChallengeService.TaskWeaponsOfChoiceEnum.Sql
  const showChallenge = allowedWeapons.length > 1

  const [navKey, setNavKey] = useState<SettingsNavKey>(showChallenge ? 'challenge' : 'appearance')

  // Tracks dwell time and aggregate counters for the close event.
  const openedAtRef = useRef<number | null>(null)
  const changesMadeRef = useRef(0)
  const navSwitchesRef = useRef(0)

  useEffect(() => {
    if (!showChallenge && navKey === 'challenge') setNavKey('appearance')
  }, [showChallenge, navKey])

  // One-shot: when something requested a specific landing tab and we just opened, apply it.
  useEffect(() => {
    if (!open || !initialTab) return
    if (initialTab === 'challenge' && !showChallenge) {
      setInitialTab(null)
      return
    }
    setNavKey(initialTab)
    setInitialTab(null)
  }, [open, initialTab, showChallenge, setInitialTab])

  const handleOpenChange = (next: boolean) => {
    if (next) {
      openedAtRef.current = Date.now()
      changesMadeRef.current = 0
      navSwitchesRef.current = 0
    } else if (openedAtRef.current != null) {
      trackWorkspaceSettings('workspace_settings:close', {
        challengeId: challenge?.id,
        taskId: task?.id,
        dwellMs: Date.now() - openedAtRef.current,
        lastNavKey: navKey,
        changesMade: changesMadeRef.current,
        navSwitches: navSwitchesRef.current,
      })
      openedAtRef.current = null
    }
    setOpen(next)
  }

  // Close settings, then open feedback on the next tick. Opening both in the same
  // event loop lets the closing dialog's focus/pointer-dismiss reach the new one and
  // instantly close it, so we wait for settings to unmount first.
  const handleSendFeedback = () => {
    handleOpenChange(false)
    setTimeout(() => openVoice({ source: 'workspace_settings', presetTopic: 'workspace' }), 200)
  }

  const handleNavSelect = (next: SettingsNavKey) => {
    if (next === navKey) return
    navSwitchesRef.current += 1
    trackWorkspaceSettings('workspace_settings:nav', {
      challengeId: challenge?.id,
      from: navKey,
      to: next,
    })
    setNavKey(next)
  }

  const handleChosenWeaponChange = async (weapon: ChallengeService.TaskWeaponsOfChoiceEnum) => {
    if (!challenge?.id) return
    const previousWeapon = chosenWeapon
    if (previousWeapon === weapon) return
    const nextWorkState = { ...(challenge.workState ?? {}), chosenWeapon: weapon }
    setChallenge({ ...challenge, workState: nextWorkState })
    changesMadeRef.current += 1
    trackWorkspaceSettings('workspace_settings:challenge:weapon_change', {
      challengeId: challenge.id,
      taskId: task?.id,
      from: previousWeapon,
      to: weapon,
      allowedWeapons,
    })
    try {
      await apis.challengeService.saveWork({
        challengeId: challenge.id,
        workState: nextWorkState,
      })
    } catch (e) {
      console.error('Failed to persist chosenWeapon', e)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        width="none"
        noMinHeight
        className="!p-0 w-[720px] sm:w-[720px] sm:max-w-[720px] h-[480px] sm:h-[480px] sm:max-h-[480px] sm:!rounded-sm"
      >
        <div className="flex h-full w-full min-h-0 flex-row overflow-hidden">
          <WorkspaceSettingsNav
            active={navKey}
            onSelect={handleNavSelect}
            showChallenge={showChallenge}
            onSendFeedback={handleSendFeedback}
          />

          <div className="flex-1 overflow-y-auto p-6">
            {navKey === 'challenge' && showChallenge && (
              <ChallengePanel
                allowedWeapons={allowedWeapons}
                chosenWeapon={chosenWeapon}
                onChosenWeaponChange={handleChosenWeaponChange}
              />
            )}
            {navKey === 'appearance' && <AppearancePanel onChange={() => { changesMadeRef.current += 1 }} />}
            {navKey === 'behavior' && <BehaviorPanel onChange={() => { changesMadeRef.current += 1 }} />}
            {navKey === 'python' && <PythonPanel onChange={() => { changesMadeRef.current += 1 }} />}
            {navKey === 'shortcuts' && <ShortcutsPanel />}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
